import type { CookieConsentState } from './cookieConsent';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    fbq?: (...args: unknown[]) => void;
    _fbq?: (...args: unknown[]) => void;
  }
}

const SCRIPT_IDS = {
  ga4: 'tv-ga4-script',
  gtm: 'tv-gtm-script',
  metaPixel: 'tv-meta-pixel-script',
};

function appendScript(id: string, src: string, onLoad?: () => void): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.getElementById(id)) {
      onLoad?.();
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.id = id;
    script.src = src;
    script.async = true;
    script.onload = () => {
      onLoad?.();
      resolve();
    };
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(script);
  });
}

function ensureGtagStub() {
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function gtag(...args: unknown[]) {
      (window.dataLayer as unknown[]).push(args);
    };
  }
}

async function loadGa4IfNeeded() {
  const measurementId = import.meta.env.VITE_GA4_MEASUREMENT_ID as string | undefined;
  if (!measurementId) return;

  ensureGtagStub();
  await appendScript(SCRIPT_IDS.ga4, `https://www.googletagmanager.com/gtag/js?id=${measurementId}`);
  window.gtag?.('js', new Date());
  window.gtag?.('config', measurementId, {
    anonymize_ip: true,
    allow_google_signals: false,
  });
}

async function loadGtmIfNeeded() {
  const gtmId = import.meta.env.VITE_GTM_ID as string | undefined;
  if (!gtmId) return;

  ensureGtagStub();
  await appendScript(SCRIPT_IDS.gtm, `https://www.googletagmanager.com/gtm.js?id=${gtmId}`);
}

async function loadMetaPixelIfNeeded() {
  const pixelId = import.meta.env.VITE_META_PIXEL_ID as string | undefined;
  if (!pixelId) return;

  if (!window.fbq) {
    ((f: Window) => {
      const n: any = function (...args: unknown[]) {
        (n.callMethod ? n.callMethod : n.queue.push).apply(n, args);
      };
      if ((f as any).fbq) return;
      n.push = n;
      n.loaded = true;
      n.version = '2.0';
      n.queue = [];
      f.fbq = n;
      f._fbq = n;
    })(window);
  }

  await appendScript(SCRIPT_IDS.metaPixel, 'https://connect.facebook.net/en_US/fbevents.js', () => {
    window.fbq?.('init', pixelId);
    window.fbq?.('track', 'PageView');
  });
}

export async function syncTrackingByConsent(consent: CookieConsentState): Promise<void> {
  try {
    if (consent.analytics) {
      await Promise.all([loadGa4IfNeeded(), loadGtmIfNeeded()]);
    }

    if (consent.marketing) {
      await loadMetaPixelIfNeeded();
      window.fbq?.('consent', 'grant');
    } else if (window.fbq) {
      window.fbq('consent', 'revoke');
    }
  } catch (error) {
    console.warn('Failed to sync tracking scripts by consent:', error);
  }
}
